<template>
  <!-- Cover Image Style -->
  <div
    v-if="variant === 'cover'"
    class="dark:ring-focus-inverse/10 duration-300 flex flex-col group h-full hover:shadow-xl overflow-hidden relative ring-1 ring-focus-level-900/5 shadow-lg transition-all w-full"
    :class="[cardRadiusClass]"
  >
    <!-- Full Cover Image Background -->
    <div class="h-full min-h-[400px] overflow-hidden relative w-full">
      <nuxt-img
        v-if="shouldShowCoverImage"
        :src="store.cover || ''"
        v-bind="lazyImgAttrs(store.name)"
        class="absolute duration-300 group-hover:scale-105 h-full inset-0 object-cover transition-transform w-full"
        quality="50"
        format="webp"
        width="800"
        height="600"
        @error="handleCoverImageError"
      />

      <div
        v-else
        class="absolute bg-gradient-to-br flex from-primary/10 h-full inset-0 items-center justify-center to-primary/3 w-full"
      >
        <BuildingStorefrontIcon class="h-24 text-primary/30 w-24" />
      </div>

      <!-- Dark Gradient Overlay at Bottom -->
      <div
        class="absolute bg-gradient-to-t bottom-0 from-surface-scrim/80 inset-x-0 pb-6 pt-20 px-6 to-surface-clear via-surface-scrim/60"
      >
        <!-- Store Name and Details -->
        <div class="relative z-(--z-index-ds-raised)">
          <h3 class="font-semibold line-clamp-2 mb-2 text-content-inverse text-lg">
            {{ store.name }}
          </h3>

          <div class="space-y-2">
            <!-- Rating and Location -->
            <div class="flex items-center text-content-inverse/90 text-sm">
              <template v-if="showRating && hasStoreReviews(store.review_count)">
                <svg
                  class="fill-icon-current h-4 mr-1 text-warning-400 w-4"
                  viewBox="0 0 20 20"
                >
                  <path
                    d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                  />
                </svg>

                <span class="font-medium">{{ store.average_rating }}</span>
              </template>

              <template
                v-if="
                  showRating && hasStoreReviews(store.review_count) && (store.city || store.state)
                "
              >
                <span class="mx-2">·</span>
              </template>

              <template v-if="store.city || store.state">
                <span>
                  {{ store.city }}<template v-if="store.city && store.state">, </template
                  >{{ store.state }}
                </span>
              </template>
            </div>

            <div
              v-if="hasGroupDiningAvailability"
              class="text-content-inverse/80 text-sm"
            >
              <span
                >{{ OrderTypePresentation[OrderType.DINE_IN].label }}
                {{ $t('dashboard.states.available') }}</span
              >
            </div>
          </div>

          <!-- CTA Button -->
          <div
            v-if="showCTA"
            class="border-border-inverse/20 border-t mt-4 pt-4"
          >
            <div class="flex justify-end">
              <span
                class="font-semibold group-hover:text-content-inverse/80 inline-flex items-center text-content-inverse text-sm"
              >
                {{ ctaText }}
                <ChevronRightIcon
                  class="group-hover:translate-x-1 h-4 ml-2 transition-transform w-4"
                />
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Favorite Button -->
      <button
        v-if="showFavorite"
        type="button"
        class="absolute bg-surface-scrim/30 hover:bg-surface-scrim/50 hover:text-danger-500 p-2 right-4 rounded-full shadow-lg text-content-inverse top-4 transition-colors z-(--z-index-ds-raised)"
        :aria-pressed="isStoreFavorite"
        :aria-label="
          isStoreFavorite
            ? $t('commerce.storeSlug.removeFavoriteLabel')
            : $t('commerce.storeSlug.saveFavoriteLabel')
        "
        @click.stop="handleFavoriteClick"
      >
        <HeartIcon
          :key="`heart-cover-${store.id}-${isStoreFavorite}`"
          class="h-5 w-5"
          :class="isStoreFavorite ? 'fill-danger-500' : ''"
        />
      </button>
    </div>

    <!-- Full-card link, kept below the favorite button in stacking order -->
    <CardLinkOverlay
      v-if="to"
      :to="to"
      :label="store.name"
      @click="handleClick"
    />
  </div>

  <!-- Grid/Card View -->
  <div
    v-else-if="variant === 'grid'"
    class="bg-surface-canvas dark:bg-surface-level-800 dark:ring-focus-inverse/10 duration-300 flex flex-col group h-full hover:shadow-xl overflow-hidden relative ring-1 ring-focus-level-900/5 shadow-lg transition-all w-full"
    :class="[cardRadiusClass]"
  >
    <!-- Store Image -->
    <div
      class="bg-gradient-to-br from-primary/10 h-48 overflow-hidden relative to-primary/3 w-full"
    >
      <nuxt-img
        v-if="shouldShowCoverImage"
        :src="store.cover || ''"
        v-bind="lazyImgAttrs(store.name)"
        class="absolute duration-300 group-hover:scale-105 h-full inset-0 object-cover transition-transform w-full"
        quality="80"
        format="webp"
        width="600"
        height="400"
        @error="handleCoverImageError"
      />

      <div
        v-else
        class="absolute flex inset-0 items-center justify-center"
      >
        <BuildingStorefrontIcon class="h-16 text-primary/30 w-16" />
      </div>
      <!-- Favorite Button -->
      <button
        v-if="showFavorite"
        type="button"
        class="absolute bg-surface-canvas dark:bg-surface-level-800 p-1.5 right-4 rounded-full shadow-sm top-4 transition-colors z-(--z-index-ds-raised)"
        :class="
          isStoreFavorite
            ? 'text-danger-500 hover:text-content-level-400'
            : 'text-content-level-400 dark:text-content-level-500 hover:text-danger-500'
        "
        :aria-pressed="isStoreFavorite"
        :aria-label="
          isStoreFavorite
            ? $t('commerce.storeSlug.removeFavoriteLabel')
            : $t('commerce.storeSlug.saveFavoriteLabel')
        "
        @click.stop="handleFavoriteClick"
      >
        <HeartIcon
          :key="`heart-grid-${store.id}-${isStoreFavorite}`"
          class="h-5 w-5"
          :class="isStoreFavorite ? 'fill-danger-500' : ''"
        />
      </button>
    </div>

    <!-- Store Content -->
    <div class="flex flex-1 flex-col p-6">
      <!-- Store Name -->
      <h3
        class="dark:text-content-inverse font-semibold group-hover:text-primary line-clamp-2 mb-3 text-content-level-900 text-lg transition-colors"
      >
        {{ store.name }}
      </h3>

      <!-- Store Details -->
      <div class="mb-4 space-y-2">
        <!-- Location -->
        <div
          v-if="showAddress && store.address"
          class="dark:text-content-level-400 flex items-center text-content-level-600 text-sm"
        >
          <MapPinIcon class="flex-shrink-0 h-4 mr-2 w-4" />

          <span class="truncate">{{ store.address }}</span>
        </div>

        <!-- Cuisine Type -->
        <div
          v-if="showCuisine && (cuisineLabel(store.cuisine) || cuisineLabel(store.cuisine_type))"
          class="dark:text-content-level-400 flex items-center text-content-level-600 text-sm"
        >
          <span class="font-medium text-primary">{{
            cuisineLabel(store.cuisine) || cuisineLabel(store.cuisine_type)
          }}</span>
        </div>

        <!-- Rating, Distance, Delivery Info -->
        <div
          class="dark:text-content-level-400 flex gap-3 items-center text-content-level-600 text-sm"
        >
          <div
            v-if="showRating && hasStoreReviews(store.review_count)"
            class="flex items-center"
          >
            <span class="font-medium">{{ store.average_rating }}</span>

            <svg
              class="h-4 ml-1 text-warning-400 w-4"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
              />
            </svg>
          </div>

          <div
            v-if="
              showDistance && store.distance_miles !== undefined && store.distance_miles !== null
            "
            class="flex items-center"
          >
            <span>{{
              $t('commerce.storeCard.distanceMi', {
                distance: Number(store.distance_miles).toFixed(1)
              })
            }}</span>
          </div>

          <div
            v-if="hasGroupDiningAvailability"
            class="flex items-center"
          >
            <span
              >{{ OrderTypePresentation[OrderType.DINE_IN].label }}
              {{ $t('dashboard.states.available') }}</span
            >
          </div>
        </div>
      </div>

      <!-- CTA Button -->
      <div
        v-if="showCTA"
        class="border-border-level-200 border-t dark:border-border-level-700 mt-auto pt-4"
      >
        <div class="flex justify-end">
          <span
            class="font-semibold group-hover:text-primary/80 inline-flex items-center text-primary text-sm"
          >
            {{ ctaText }}
            <ChevronRightIcon class="group-hover:translate-x-1 h-4 ml-2 transition-transform w-4" />
          </span>
        </div>
      </div>
    </div>

    <!-- Full-card link, kept below the favorite button in stacking order -->
    <CardLinkOverlay
      v-if="to"
      :to="to"
      :label="store.name"
      @click="handleClick"
    />
  </div>

  <!-- Search Results View -->
  <div
    v-else-if="variant === 'search'"
    class="bg-surface-canvas dark:bg-surface-level-800 hover:shadow-lg overflow-hidden relative shadow-sm transition-shadow"
    :class="[cardRadiusClass]"
  >
    <!-- Restaurant Image -->
    <div
      class="bg-surface-level-200 dark:bg-surface-level-700 h-[150px] md:h-48 overflow-hidden relative w-full"
      :class="[imageTopRadiusClass]"
    >
      <nuxt-img
        v-if="shouldShowCoverImage"
        :src="store.cover || ''"
        v-bind="lazyImgAttrs(store.name)"
        class="h-full object-cover w-full"
        quality="80"
        format="webp"
        width="600"
        height="400"
        @error="handleCoverImageError"
      />

      <div
        v-else
        class="flex h-full items-center justify-center w-full"
      >
        <BuildingStorefrontIcon
          class="dark:text-content-level-500 h-16 text-content-level-400 w-16"
        />
      </div>
    </div>

    <!-- Restaurant Info -->
    <div class="p-4 text-[13px]">
      <div class="flex items-start justify-between mb-[5px]">
        <h4
          class="dark:text-content-inverse font-semibold text-[15px] text-content-level-900 truncate"
        >
          {{ store.name }}
        </h4>

        <button
          v-if="showFavorite"
          type="button"
          class="relative transition-colors z-(--z-index-ds-raised)"
          :class="
            isStoreFavorite
              ? 'text-danger-500 hover:text-content-level-400'
              : 'text-content-level-400 dark:text-content-level-500 hover:text-danger-500'
          "
          :aria-pressed="isStoreFavorite"
          :aria-label="
            isStoreFavorite
              ? $t('commerce.storeSlug.removeFavoriteLabel')
              : $t('commerce.storeSlug.saveFavoriteLabel')
          "
          @click.stop="handleFavoriteClick"
        >
          <HeartIcon
            :key="`heart-search-${store.id}-${isStoreFavorite}`"
            class="h-6 w-6"
            :class="isStoreFavorite ? 'fill-danger-500 hover:dark:fill-none' : ''"
          />
        </button>
      </div>

      <!-- Rating, Distance, Delivery Fee -->
      <div
        class="dark:text-content-level-300 flex items-center mb-[5px] text-[13px] text-content-level-600"
      >
        <template v-if="showRating && hasStoreReviews(store.review_count)">
          <svg
            class="fill-icon-current h-4 mr-1 text-warning-400 w-4"
            viewBox="0 0 20 20"
          >
            <path
              d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
            />
          </svg>

          <span class="font-medium">{{ store.average_rating }}</span>
        </template>
        <!-- Dot between rating and distance (if distance is shown) -->
        <template
          v-if="
            showRating &&
            hasStoreReviews(store.review_count) &&
            showDistance &&
            Number.isFinite(Number(store.distance_miles))
          "
        >
          <span class="mx-2">·</span>
        </template>
        <!-- Dot between rating and group dining (if distance is NOT shown) -->
        <template v-else-if="hasGroupDiningAfterRating">
          <span class="mx-2">·</span>
        </template>

        <template v-if="showDistance && Number.isFinite(Number(store.distance_miles))">
          <span>{{
            $t('commerce.storeCard.distanceMi', {
              distance: Number(store.distance_miles).toFixed(1)
            })
          }}</span>
        </template>
        <!-- Dot between distance and group dining -->
        <template v-if="hasDistanceValue && hasGroupDiningAvailability">
          <span class="mx-2">·</span>
        </template>

        <template v-if="hasGroupDiningAvailability">
          <span
            >{{ OrderTypePresentation[OrderType.DINE_IN].label }}
            {{ $t('dashboard.states.available') }}</span
          >
        </template>
      </div>

      <!-- Address -->
      <p
        v-if="showAddress && store.address"
        class="dark:text-content-level-400 mb-[10px] text-[13px] text-content-level-500"
      >
        {{ store.address }}{{ store.city ? `, ${store.city}` : ''
        }}{{ store.state ? `, ${store.state}` : '' }}{{ store.zip ? ` ${store.zip}` : '' }}
      </p>

      <!-- Special Badge (if applicable) -->
      <!--
        <div
        v-if="
        showDistance &&
        showDistanceBadge &&
        store.distance_miles !== undefined &&
        store.distance_miles !== null &&
        store.distance_miles < 10
        "
        class="inline-block bg-badge-delivery-fast text-content-inverse text-xs font-medium px-2 py-1 rounded mb-4"
        >
        Fast Delivery
        </div>
      -->

      <!-- CTA Button -->
      <div
        v-if="showCTA"
        class="mt-1"
      >
        <div class="flex justify-end">
          <span
            class="font-semibold group-hover:text-primary/80 inline-flex items-center text-primary text-sm"
          >
            {{ ctaText }}
            <ChevronRightIcon class="group-hover:translate-x-1 h-4 ml-2 transition-transform w-4" />
          </span>
        </div>
      </div>
    </div>

    <!-- Full-card link, kept below the favorite button in stacking order -->
    <CardLinkOverlay
      v-if="to"
      :to="to"
      :label="store.name"
      @click="handleClick"
    />
  </div>

  <!-- List View -->
  <div
    v-else
    class="bg-surface-canvas border border-border-level-200 dark:bg-surface-level-800 dark:border-border-level-700 group hover:shadow-lg relative rounded-lg transition-shadow"
  >
    <div class="flex flex-col md:flex-row overflow-hidden">
      <!-- Store Image -->
      <div
        class="bg-gradient-to-br flex flex-shrink-0 from-primary/10 items-center justify-center md:h-auto md:w-48 overflow-hidden relative to-primary/3 w-full"
        :class="[listImageRadiusClass]"
      >
        <nuxt-img
          v-if="shouldShowCoverImage"
          :src="store.cover || ''"
          v-bind="lazyImgAttrs(store.name)"
          class="h-full object-cover w-full"
          quality="80"
          format="webp"
          width="400"
          height="300"
          @error="handleCoverImageError"
        />

        <BuildingStorefrontIcon
          v-else
          class="dark:text-content-level-500 h-12 sm:h-16 sm:w-16 text-content-level-400 w-12"
        />
        <!-- Favorite Button for Mobile List View -->
        <button
          v-if="showFavorite"
          type="button"
          class="absolute bg-surface-canvas dark:bg-surface-level-800 md:hidden p-1.5 right-2 rounded-full shadow-sm top-2 transition-colors z-(--z-index-ds-raised)"
          :class="
            isStoreFavorite
              ? 'text-danger-500 hover:text-content-level-400'
              : 'text-content-level-400 dark:text-content-level-500 hover:text-danger-500'
          "
          :aria-pressed="isStoreFavorite"
          :aria-label="
            isStoreFavorite
              ? $t('commerce.storeSlug.removeFavoriteLabel')
              : $t('commerce.storeSlug.saveFavoriteLabel')
          "
          @click.stop="handleFavoriteClick"
        >
          <HeartIcon
            :key="`heart-list-mobile-${store.id}-${isStoreFavorite}`"
            class="h-4 w-4"
            :class="isStoreFavorite ? 'fill-danger-500' : ''"
          />
        </button>
      </div>

      <!-- Store Info -->
      <div class="flex flex-1 flex-col p-4">
        <div class="flex items-start justify-between">
          <div class="flex-1">
            <!-- Store Name -->
            <h3
              class="dark:text-content-inverse font-semibold group-hover:text-primary line-clamp-2 mb-3 text-content-level-900 text-lg transition-colors"
            >
              {{ store.name }}
            </h3>

            <!-- Store Details - Single Row -->
            <div
              class="dark:text-content-level-400 flex flex-wrap gap-x-4 gap-y-2 items-center text-content-level-600 text-sm"
            >
              <!-- Location -->
              <div
                v-if="showAddress && store.address"
                class="flex items-center"
              >
                <MapPinIcon class="flex-shrink-0 h-4 mr-1.5 w-4" />

                <span class="truncate">{{ store.address }}</span>
              </div>

              <!-- Cuisine Type -->
              <div
                v-if="
                  showCuisine && (cuisineLabel(store.cuisine) || cuisineLabel(store.cuisine_type))
                "
                class="flex items-center"
              >
                <span class="font-medium text-primary">{{
                  cuisineLabel(store.cuisine) || cuisineLabel(store.cuisine_type)
                }}</span>
              </div>

              <!-- Rating -->
              <div
                v-if="showRating && hasStoreReviews(store.review_count)"
                class="flex items-center"
              >
                <span class="font-medium">{{ store.average_rating }}</span>

                <svg
                  class="h-4 ml-1 text-warning-400 w-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                  />
                </svg>
              </div>

              <!-- Distance -->
              <div
                v-if="
                  showDistance &&
                  store.distance_miles !== undefined &&
                  store.distance_miles !== null
                "
                class="flex items-center"
              >
                <span>{{
                  $t('commerce.storeCard.distanceMi', {
                    distance: Number(store.distance_miles).toFixed(1)
                  })
                }}</span>
              </div>

              <div
                v-if="hasGroupDiningAvailability"
                class="flex items-center"
              >
                <span
                  >{{ OrderTypePresentation[OrderType.DINE_IN].label }}
                  {{ $t('dashboard.states.available') }}</span
                >
              </div>
            </div>
          </div>

          <!-- Favorite Button for Desktop -->
          <button
            v-if="showFavorite"
            type="button"
            class="flex-shrink-0 hidden md:block ml-4 relative transition-colors z-(--z-index-ds-raised)"
            :class="
              isStoreFavorite
                ? 'text-danger-500 hover:text-content-level-400'
                : 'text-content-level-400 dark:text-content-level-500 hover:text-danger-500'
            "
            :aria-pressed="isStoreFavorite"
            :aria-label="
              isStoreFavorite
                ? $t('commerce.storeSlug.removeFavoriteLabel')
                : $t('commerce.storeSlug.saveFavoriteLabel')
            "
            @click.stop="handleFavoriteClick"
          >
            <HeartIcon
              :key="`heart-list-desktop-${store.id}-${isStoreFavorite}`"
              class="h-5 w-5"
              :class="isStoreFavorite ? 'fill-danger-500' : ''"
            />
          </button>
        </div>

        <!-- CTA Button -->
        <div
          v-if="showCTA"
          class="mt-auto pt-4"
        >
          <div class="flex justify-end">
            <span
              class="font-semibold group-hover:text-primary/80 inline-flex items-center text-primary text-sm"
            >
              {{ ctaText }}
              <ChevronRightIcon
                class="group-hover:translate-x-1 h-4 ml-2 transition-transform w-4"
              />
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Full-card link, kept below the favorite button in stacking order -->
    <CardLinkOverlay
      v-if="to"
      :to="to"
      :label="store.name"
      @click="handleClick"
    />
  </div>
</template>

<script setup lang="ts" generic="T extends object">
import {
  BuildingStorefrontIcon,
  ChevronRightIcon,
  HeartIcon,
  MapPinIcon
} from '@heroicons/vue/24/outline'
import { computed, ref, watch } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import CardLinkOverlay from '~/components/shared/CardLinkOverlay.vue'
import { OrderType } from '~/enums'
import { OrderTypePresentation } from '~/presentation/OrderTypePresentation'
import { useIdentity } from '~/composables/useIdentity'
import { hasStoreReviews } from '~/utils/hasStoreReviews'

interface StoreCardModel {
  id: string | number
  name?: string
  cover?: string
  average_rating: number | null
  review_count: number
  city?: string
  state?: string
  zip?: string
  address?: string
  cuisine?: string | { name?: string }
  cuisine_type?: string | { name?: string }
  dine_in_available?: boolean
  distance_miles?: number | string
  is_favorite?: boolean
}
type CardRadius = 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full'
const props = withDefaults(
  defineProps<{
    /** Store data displayed by the card. */
    store: T & StoreCardModel
    /** Card layout variant. */
    variant?: 'grid' | 'list' | 'cover' | 'search'
    /** Whether to show the rating. */
    showRating?: boolean
    /** Whether to show the Group Dining availability line. */
    showGroupDining?: boolean
    /** Whether to show cuisine information. */
    showCuisine?: boolean
    /** Whether to show the address. */
    showAddress?: boolean
    /** Whether to show distance information. */
    showDistance?: boolean
    /** Whether to show the favorite control. */
    showFavorite?: boolean
    /** Whether to show the call-to-action. */
    showCTA?: boolean
    /** Label for the call-to-action. */
    ctaText?: string
    /** Optional card corner radius variant. */
    cardRadius?: CardRadius | null
    /** Store route the card links to; the card renders no link when absent. */
    to?: RouteLocationRaw | null
  }>(),
  {
    cardRadius: null,
    ctaText: 'View Menu',
    showAddress: false,
    showCTA: false,
    showCuisine: false,
    showDistance: false,
    showFavorite: false,
    showGroupDining: false,
    showRating: false,
    to: null,
    variant: 'grid'
  }
)

const emit = defineEmits<{
  storeSelected: [store: T & StoreCardModel]
  favorite: [store: T & StoreCardModel]
}>()

// NuxtImg's declared prop type doesn't include native img attrs like `loading`;
// they still forward correctly via $attrs at runtime.
const lazyImgAttrs = (alt: string | undefined) => ({ alt: alt ?? '', loading: 'lazy' }) as const
const { isFavorite } = useFavorites()
const { isAuthenticated } = useIdentity()

const isStoreFavorite = computed(() => {
  if (isAuthenticated.value) {
    return props.store.is_favorite
  }
  return typeof props.store.id === 'number' ? isFavorite(props.store.id) : false
})

const coverImageFailed = ref(false)
const handleCoverImageError = () => {
  coverImageFailed.value = true
}
const cuisineLabel = (value: string | { name?: string } | undefined): string | undefined =>
  typeof value === 'string' ? value : value?.name

const shouldShowCoverImage = computed(() => {
  const cover = props.store.cover
  return cover !== null && cover !== undefined && cover.length > 0 && !coverImageFailed.value
})

const hasDistanceValue = computed(
  () => props.showDistance && Number.isFinite(Number(props.store.distance_miles))
)
const hasGroupDiningAvailability = computed(
  () => props.showGroupDining && props.store.dine_in_available === true
)
const hasGroupDiningAfterRating = computed(
  () =>
    props.showRating &&
    hasStoreReviews(props.store.review_count) &&
    !hasDistanceValue.value &&
    hasGroupDiningAvailability.value
)
watch(
  () => props.store.cover,
  () => {
    coverImageFailed.value = false
  }
)

// Radius class mapping
const radiusClasses = {
  '2xl': 'rounded-2xl',
  '3xl': 'rounded-3xl',
  full: 'rounded-full',
  lg: 'rounded-lg',
  md: 'rounded-md',
  none: '',
  sm: 'rounded-sm',
  xl: 'rounded-xl'
}

const topRadiusClasses = {
  '2xl': 'rounded-t-2xl',
  '3xl': 'rounded-t-3xl',
  full: 'rounded-t-full',
  lg: 'rounded-t-lg',
  md: 'rounded-t-md',
  none: '',
  sm: 'rounded-t-sm',
  xl: 'rounded-t-xl'
}

const listImageRadiusClasses = {
  '2xl': 'rounded-t-2xl md:rounded-l-2xl md:rounded-tr-none',
  '3xl': 'rounded-t-3xl md:rounded-l-3xl md:rounded-tr-none',
  full: 'rounded-t-full md:rounded-l-full md:rounded-tr-none',
  lg: 'rounded-t-lg md:rounded-l-lg md:rounded-tr-none',
  md: 'rounded-t-md md:rounded-l-md md:rounded-tr-none',
  none: 'rounded-t-lg md:rounded-l-lg md:rounded-tr-none',
  sm: 'rounded-t-sm md:rounded-l-sm md:rounded-tr-none',
  xl: 'rounded-t-xl md:rounded-l-xl md:rounded-tr-none'
}

// Computed radius classes based on variant and prop
const cardRadiusClass = computed(() => {
  if (props.cardRadius) {
    return radiusClasses[props.cardRadius] || ''
  }
  // Default radius based on variant
  if (props.variant === 'cover' || props.variant === 'grid') {
    return 'rounded-2xl'
  }
  return 'rounded-lg'
})

const imageTopRadiusClass = computed(() => {
  if (props.cardRadius) {
    return topRadiusClasses[props.cardRadius] || ''
  }
  return 'rounded-t-lg'
})

const listImageRadiusClass = computed(() => {
  if (props.cardRadius) {
    return (
      listImageRadiusClasses[props.cardRadius] || 'rounded-t-lg md:rounded-l-lg md:rounded-tr-none'
    )
  }
  return 'rounded-t-lg md:rounded-l-lg md:rounded-tr-none'
})

const handleClick = () => {
  emit('storeSelected', props.store)
}

const handleFavoriteClick = async (event: MouseEvent) => {
  event.stopPropagation()
  emit('favorite', props.store)
}
</script>
